import { Component } from '@theme/component';
import { morphSection } from '@theme/section-renderer';

class ProductRecommendations extends Component {
  /**
   * The observer for the product recommendations
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      // Only attribute changes are interesting
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      // Ignore error attribute changes
      if (mutation.attributeName === 'data-error') continue;

      // Ignore addition of hidden class because it means there's an error with the display
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      // Ignore when the data-recommendations-performed attribute has been set to 'true'
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      // All other attribute changes trigger a reload
      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    super.connectedCallback();
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver.disconnect();
    this.#mutationObserver.disconnect();
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;

    if (!productId || !sectionId) {
      throw new Error('Product ID and a section ID are required');
    }

    // If the recommendations have already been loaded, accounts for the case where the Theme Editor
    // is loaded the section from the editor's visual preview context.
    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then(async (result) => {
        if (!result.success) {
          // The Theme Editor will place a section element element in the DOM whose section_id is not available
          // to the Section Renderer API. In this case, we can safely ignore the error.
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        if (result.data?.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          await morphSection(sectionId, result.data, { mode: 'hydration', injectStylesheet: true });
          await this.#rankCards();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  async #rankCards() {
    const cards = [...this.querySelectorAll('.resource-list__item')];
    if (cards.length < 3) return;
    const cart = await fetch('/cart.js').then((response) => response.json()).catch(() => ({ total_price: 0 }));
    const cartTotal = Number(cart.total_price || 0);
    const printWords = ['bird', 'flower', 'floral', 'nature', 'plant', 'garden', 'heron', 'robin', 'duck', 'leaf', 'wing'];
    const typeWords = ['quarter-?zip', 'quarterzip', 'sweatshirt', 'hoodie', 't-?shirt', 'tee', 'sun protection', 'sun protective'];
    const ranked = await Promise.all(cards.map(async (card, index) => {
      const link = card.querySelector('a[href*="/products/"]');
      const handle = link?.getAttribute('href')?.split('/products/')[1]?.split(/[?#/]/)[0];
      const product = handle ? await fetch(`/products/${handle}.js`).then((response) => response.json()).catch(() => null) : null;
      const text = `${product?.title || card.textContent || ''} ${(product?.tags || []).join(' ')}`.toLowerCase();
      const productType = `${product?.type || ''}`.toLowerCase();
      const type = productType.includes('quarterzip') || productType.includes('quarter-zip')
        ? 0
        : typeWords.findIndex((word) => new RegExp(word).test(text));
      if (productType.includes('quarterzip') || productType.includes('quarter-zip')) {
        const displayTitle = product.title.replace(/stretch sweatshirt/gi, 'Stretch Pullover');
        for (const element of card.querySelectorAll('a, p, span, h2, h3, h4')) {
          if (element.children.length === 0 && element.textContent.includes('Stretch Sweatshirt')) {
            element.textContent = element.textContent.replace(/stretch sweatshirt/gi, 'Stretch Pullover');
          }
        }
      }
      const print = printWords.some((word) => text.includes(word));
      const price = Number(product?.price || 0);
      const bundle = Math.abs(5000 - (cartTotal + price));
      return { card, index, type: type >= 0 ? 0 : 1, print: print ? 0 : 1, bundle };
    }));
    const buckets = [
      ranked.filter((item) => item.type === 0),
      ranked.filter((item) => item.print === 0 && item.type !== 0),
      ranked.filter((item) => item.type !== 0 && item.print !== 0).sort((a, b) => a.bundle - b.bundle),
    ];
    const ordered = [];
    while (buckets.some((bucket) => bucket.length)) {
      for (const bucket of buckets) {
        if (bucket.length) ordered.push(bucket.shift());
      }
    }
    const list = cards[0].parentElement;
    for (const item of ordered) list.append(item.card);
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
